import useEscapeKey from "@hooks/useEscapeKey";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { G_ATTRIBUTES } from "../../constants";
import { useTheme } from "@hooks/useTheme";
import TableForUIPagination from "../Tables/TableForUIPagination";
import { createPortal } from "react-dom";

const PolicyConflictModal = ({
  isOpen,
  onClose,
  licensePolicyResult = {},
  "data-testid": dataTestId = "policy-conflict-modal",
}) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  useEscapeKey(onClose, isOpen);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Helper function to get attribute remark from translation files
  const getAttributeRemark = (attrCode) => {
    try {
      const attrMapping = {
        ATTR_01: "license-policy.attr-1",
        ATTR_02_ALL: "license-policy.attr-2-all",
        ATTR_02_FILE: "license-policy.attr-2-file",
        ATTR_02_LIBRARY: "license-policy.attr-2-library",
        ATTR_02_MODULE: "license-policy.attr-2-module",
        ATTR_03: "license-policy.attr-3",
        ATTR_04: "license-policy.attr-4",
        ATTR_05: "license-policy.attr-5",
        ATTR_06: "license-policy.attr-6",
        ATTR_07: "license-policy.attr-7",
        ATTR_08: "license-policy.attr-8",
        ATTR_09: "license-policy.attr-9",
        ATTR_10: "license-policy.attr-10",
        ATTR_11: "license-policy.attr-11",
        ATTR_12: "license-policy.attr-12",
        ATTR_13: "license-policy.attr-13",
      };

      // Get the license-policy translation key
      const translationKey = attrMapping[attrCode];
      if (translationKey) {
        const translation = t(translationKey);
        // If translation returns the same key, it means translation not found
        if (translation !== translationKey) {
          return translation;
        }
      }

      // Fallback to g_attributes structure
      const translation = t(`g_attributes.${attrCode}.remark`);
      if (translation !== `g_attributes.${attrCode}.remark`) {
        return translation;
      }

      // Final fallback to constants
      const attribute = G_ATTRIBUTES.find((attr) => attr.code === attrCode);
      if (attribute) {
        return attribute.remark;
      }

      return attrCode;
    } catch (error) {
      // Fallback to constants if translation fails
      const attribute = G_ATTRIBUTES.find((attr) => attr.code === attrCode);
      if (attribute) {
        return attribute.remark;
      }
      return attrCode;
    }
  };

  const getApprovalTranslation = (approvalCode) => {
    const approvalMapping = {
      "APPROVAL": "project.policy-conflict-modal.approval-label",
      "DISAPPROVAL": "project.policy-conflict-modal.disapproval",
      "CONDITIONAL_APPROVAL": "project.policy-conflict-modal.conditional",
    };

    const translationKey = approvalMapping[approvalCode];
    if (translationKey) {
      const translation = t(translationKey);
      if (translation !== translationKey) {
        return translation;
      }
    }
    return approvalCode;
  };

  const rows = useMemo(() => {
    if (!licensePolicyResult?.conflictDetails || !Array.isArray(licensePolicyResult.conflictDetails)) return [];

    // Map conflictDetails to table rows
    return licensePolicyResult.conflictDetails
      .slice()
      .sort((a, b) => (a?.policyStep ?? 0) - (b?.policyStep ?? 0))
      .map((item) => ({
        step: item?.policyStep ?? "",
        approval: item?.licenseUseApprovalCd || "",
        remark: item?.remark || "",
        attributes: item?.attributes || [],
      }));
  }, [licensePolicyResult]);

  const columns = ["step", "approval", "remark"];
  const columnConfig = {
    step: {
      style: { width: "15%" },
      headerRender: () => t("project.policy-conflict-modal.step", "Policy Step"),
    },
    approval: {
      style: { width: "25%", whiteSpace: "normal" },
      headerRender: () => t("project.policy-conflict-modal.approval", "License Use Approval"),
      customRender: (value) => getApprovalTranslation(value),
    },
    remark: {
      style: { width: "60%", whiteSpace: "normal" },
      headerRender: () => t("project.policy-conflict-modal.remark", "Remark"),
      customRender: (value, row) => (
        <div className="flex flex-col">
          <span className={`text-sm ${colors.textPrimary}`}>{row.remark}</span>
          {row.attributes && row.attributes.length > 0 && (
            <div className="mt-2">
              {row.attributes.map((attr, index) => (
                <div key={index} className="flex items-start mb-0.5">
                  <span className={`${colors.textDanger} mr-2`}>-</span>
                  <span className={`${colors.textDanger} text-xs`}>{getAttributeRemark(attr)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ),
    },
  };

  const totalData = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalData / itemsPerPage));

  if (!isOpen) return null;

  const modalContent = (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start md:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`}
    >
      <div
        data-testid={dataTestId}
        className={`${colors.bgModal} max-h-[70vh] w-[840px] min-w-[840px] flex-shrink-0 rounded-lg shadow-xl flex flex-col`}
      >
        {/* Header */}
        <div className={`px-6 py-3 ${colors.borderPrimary} border-b`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
              {t("project.policy-conflict-modal.title", "License Policy Conflict Information")}
            </h2>
            <button
              type="button"
              data-testid={`${dataTestId}-close-button`}
              onClick={onClose}
              className={`p-2 ${colors.textMuted} hover:${colors.textPrimary} transition-colors`}
              aria-label="Close"
            >
              <span className="text-3xl font-bold leading-none">×</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4 flex-1 overflow-auto" data-testid={`${dataTestId}-content`}>
          <div data-testid={fieldValueTestId("conflict-table")}>
          <TableForUIPagination
            columns={columns}
            columnConfig={columnConfig}
            data={rows}
            loading={false}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            totalData={totalData}
            maxRowsPerPage={itemsPerPage}
            onItemsPerPageChange={(value) => {
              const v = typeof value === "number" ? value : value;
              setItemsPerPage(v);
              setCurrentPage(1);
            }}
            showPagination={false}
            shouldShowGoToPage={false}
            tableStyle=""
          />
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(modalContent, document.body);
};

export default PolicyConflictModal;
